import { useQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'
import { entitlementQueryKeys } from '@/hooks/entitlements/types'

import type { Entitlement } from '@/types/Entitlement'

import { useEntitlementLoader } from '@/services/entitlement-loader/context'

export interface UseEntitlementCheckOptions {
  entitlement: Entitlement
  enabled?: boolean
}

/**
 * Hook for checking if the current billing organization has a specific entitlement.
 *
 * Uses the DataLoader pattern to batch and cache entitlement checks via SpiceDB.
 *
 * @example
 * const { data: hasSharepoint, isLoading } = useEntitlementCheck({ entitlement: 'sharepoint' })
 */
export const useEntitlementCheck = ({
  entitlement,
  enabled = true,
}: UseEntitlementCheckOptions) => {
  const { accessToken, loading: authIsLoading } = useAuthInfo()
  const { data: billingOrg, isLoading: billingOrgIsLoading } =
    useBillingOrganization()
  const loader = useEntitlementLoader()

  const billingOrgId = billingOrg?.id

  const query = useQuery({
    queryKey: entitlementQueryKeys.check({
      billingOrgId,
      entitlement,
    }),
    queryFn: () =>
      loader!.load({
        billingOrgId: billingOrgId!,
        entitlement,
      }),
    enabled: !!billingOrgId && !!loader && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const hasResolvedAccessToken = accessToken !== undefined
  const hasAccessToken = accessToken !== null && accessToken !== undefined

  const prerequisitesAreLoading =
    enabled &&
    (authIsLoading ||
      (hasAccessToken && (billingOrgIsLoading || billingOrg === undefined)) ||
      (hasResolvedAccessToken &&
        hasAccessToken &&
        billingOrg !== null &&
        loader === null))

  return {
    ...query,
    isLoading: query.isLoading || prerequisitesAreLoading,
  }
}
